import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

// What the mobile app bar shows for the current page. Pages opt in with
// usePageHeader(); pages that don't get the HiLo logo instead of a title.
export interface PageHeader {
  title?: string;
  subtitle?: string;
  // Right-aligned actions, e.g. a share or filter button.
  actions?: React.ReactNode;
  // Renders a back arrow instead of the menu button. It calls `onBack` when
  // given, else history.goBack().
  back?: boolean;
  onBack?: () => void;
}

interface PageHeaderContextValue {
  header: PageHeader;
  setHeader: (header: PageHeader) => void;
}

const PageHeaderContext = createContext<PageHeaderContextValue | null>(null);

export function PageHeaderProvider ({ children }: { children: React.ReactNode }) {
  const [header, setHeaderState] = useState<PageHeader>({});
  const setHeader = useCallback((next: PageHeader) => setHeaderState(next), []);
  const value = useMemo(() => ({ header, setHeader }), [header, setHeader]);
  return <PageHeaderContext.Provider value={value}>{children}</PageHeaderContext.Provider>;
}

export function usePageHeaderValue (): PageHeader {
  const ctx = useContext(PageHeaderContext);
  return ctx ? ctx.header : {};
}

// Sets the mobile app bar contents while the calling page is mounted and
// clears them on unmount. `actions` is a React node, so callers should keep
// the dependency list honest (memoize or key on the values that matter).
export function usePageHeader (header: PageHeader, deps: React.DependencyList = []) {
  const ctx = useContext(PageHeaderContext);
  const setHeader = ctx?.setHeader;
  useEffect(() => {
    if (!setHeader) return undefined;
    setHeader(header);
    return () => setHeader({});
  // eslint-disable-next-line react-hooks/exhaustive-deps -- callers pass the deps that matter
  }, [setHeader, ...deps]);
}
